import React, { useState } from 'react';
import { ChevronRight, Search, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HelpButton from '../components/HelpButton';
import { useEffect, useRef } from 'react';
import FAQ from '../components/FAQ';
import SEO from '../components/SEO';

const categories = [
  {
    title: 'Elektrotechnische renovatie',
    questions: [
      'Hoe lang duurt een renovatie gemiddeld?',
      'Wat zijn de kosten van een renovatie?',
      'Kan ik tijdens de renovatie in mijn huis blijven wonen?',
      'Welke vergunningen zijn nodig?'
    ]
  },
  {
    title: 'Groepenkast',
    questions: [
      'Wanneer moet ik mijn groepenkast vervangen?',
      'Wat kost een nieuwe groepenkast?',
      'Hoeveel groepen heb ik nodig?',
      'Is een aardlekschakelaar verplicht?'
    ]
  },
  {
    title: 'Laadpalen',
    questions: [
      'Welke laadpaal heb ik nodig?',
      'Wat zijn de installatiekosten?',
      'Heb ik een 3-fase aansluiting nodig?',
      'Hoe lang duurt de installatie?'
    ]
  },
  {
    title: 'Zonnepanelen',
    questions: [
      'Hoeveel zonnepanelen heb ik nodig?',
      'Wat is de terugverdientijd?',
      'Welke omvormer is geschikt?',
      'Heb ik een verzwaard net nodig?'
    ]
  }
];

function VeelgesteldeVragen() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState<null | { category: string; question: string; answer: string }>(null);
  const answerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedQuestion && answerRef.current) {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 300);
    }
  }, [selectedQuestion]);

  const answers = {
    'Hoe lang duurt een renovatie gemiddeld?': 'Een gemiddelde elektrotechnische renovatie duurt 3-5 werkdagen, afhankelijk van de omvang van het project. Grote renovaties kunnen 1-2 weken duren. We maken vooraf een duidelijke planning en zorgen voor minimale overlast.',
    'Wat zijn de kosten van een renovatie?': 'De kosten van een renovatie variëren sterk en zijn afhankelijk van factoren zoals de grootte van de woning, de staat van de huidige installatie en uw specifieke wensen. Gemiddeld ligt dit tussen €2.500 en €7.500. We maken graag een gedetailleerde offerte op maat.',
    'Kan ik tijdens de renovatie in mijn huis blijven wonen?': 'Ja, in de meeste gevallen kunt u gewoon in uw huis blijven wonen tijdens de renovatie. We werken in fases en zorgen dat u aan het einde van elke werkdag weer stroom heeft. Voor grote renovaties maken we een planning die bij uw situatie past.',
    'Welke vergunningen zijn nodig?': 'Voor de meeste elektrotechnische renovaties is geen vergunning nodig. Bij aanpassingen aan de meterkast of verzwaring van de aansluiting regelen wij de benodigde meldingen bij de netbeheerder. We zorgen voor alle vereiste keuringen en certificaten.',
  };

  const filteredCategories = categories.map(category => ({
    ...category,
    questions: category.questions.filter(question =>
      question.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-white page-transition pt-[99px] md:pt-[133px]">
       <SEO
        title="Veelgestelde vragen - elektricieninarnhem026.nl"
        description="Veelgestelde vragen over onze elektrotechnische diensten. Kom je er niet uit? Neem dan contact met ons op."
        image="/images/banner.png"
      />
      <Header />
      
      <section className="mx-auto w-[calc(100%-2.5rem)] max-w-[1088px] mb-12 md:mb-20 pt-16 md:pt-24">
        <div className="relative">
          {selectedQuestion ? (
            <div ref={answerRef} className="animate-fadeIn transition-all duration-500 ease-in-out pt-4">
              <button
                onClick={() => setSelectedQuestion(null)}
                className="flex items-center text-[#FFD100] hover:underline mb-8"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Terug naar alle vragen
              </button>
              <h1 className="font-bold text-[28px] md:text-[36px] text-[#17354A] mb-6">
                {selectedQuestion.question}
              </h1>
              <div className="prose max-w-none text-[16px] md:text-[18px] text-[#17354A] leading-relaxed">
                {selectedQuestion.answer}
              </div>
            </div>
          ) : (
            <div className="animate-fadeIn">
              <h1 className="font-bold text-black leading-[1.25] text-[40px] lg:text-[52px] mb-4 text-[28px] md:mb-5 md:text-[44px] text-[#17354A]">
                Veelgestelde vragen
              </h1>
              <div className="text-black leading-[1.56] text-[16px] md:text-[18px] font-normal text-[#17354A] max-w-[720px] mb-8">
                Vind snel antwoord op al je vragen over onze elektrotechnische diensten. Kom je er niet uit? Neem dan contact met ons op.
              </div>

              {/* Search Bar */}
              <div className="mb-12">
                <div className="relative max-w-[560px]">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Zoek in veelgestelde vragen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-[#E5E7EB] rounded-xl focus:border-[#FFD100] focus:outline-none text-[#17354A]"
                  />
                </div>
              </div>

              {/* Categories Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
                {filteredCategories.map((category, index) => (
                  <div 
                    key={index}
                    className="bg-white border border-[#E5E7EB] rounded-xl p-6 hover:border-[#17354A]/50 transition-colors duration-150"
                  >
                    <h2 className="font-bold text-[20px] text-[#17354A] mb-4">
                      {category.title}
                    </h2>
                    <div className="space-y-3">
                      {category.questions.map((question, qIndex) => (
                        <button 
                          key={qIndex}
                          onClick={() => setSelectedQuestion({
                            category: category.title,
                            question,
                            answer: answers[question] || 'Antwoord wordt binnenkort toegevoegd.'
                          })}
                          className="w-full flex items-center justify-between text-[#17354A] hover:text-[#FFD100] transition-colors duration-150 group text-left"
                        >
                          <span>{question}</span>
                          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#FFD100] flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* General FAQ Section */}
        <div className={`transition-all duration-500 ease-in-out mb-24 ${
          selectedQuestion ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-[2000px]'
        }`}>
          <h2 className="font-bold text-[24px] lg:text-[36px] text-[#17354A] mb-8">
            Algemene vragen
          </h2>
          <FAQ />
        </div>

        {/* Contact CTA */}
        <div className={`bg-[#FFD100] text-white rounded-xl p-8 md:p-12 mb-12 transition-all duration-500 ease-in-out ${
          selectedQuestion ? 'opacity-0 max-h-0 overflow-hidden mt-0' : 'opacity-100 max-h-[500px] mt-12'
        }`}>
          <h2 className="font-bold text-[24px] lg:text-[36px] mb-4">
            Geen antwoord gevonden?
          </h2>
          <p className="text-[16px] md:text-[18px] mb-6">
            Neem contact met ons op. Onze experts staan klaar om al je vragen te beantwoorden.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center justify-center px-6 py-3 bg-white text-[#FFD100] rounded-full font-medium hover:bg-white/90 transition-colors"
          >
            Contact opnemen
            <ChevronRight className="w-4 h-4 ml-1" />
          </a>
        </div>
      </section>

      <HelpButton />
      <Footer />
    </div>
  );
}

export default VeelgesteldeVragen;